import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Eye, RefreshCw, Search, X } from 'lucide-react';
import BlueXLoader, { PageDataLoader } from '../../../components/BlueXLoader';
import '../early-disclosure.css';
import '../../assets/assets.css';
import '../../ghost-cve/ghost-cve.css';
import '../../vulnerabilities/vulnerabilities.css';
import '../../scans/scans.css';
import { getEarlyIntelSummary, getEarlyIntelFeatureSettings, updateEarlyIntelFeatureSettings, resetEarlyIntelFeatureSettingsMonthlyCounter } from '../../../services/early-intel.service';
import { getCachedProfile } from '../../../services/_core';
import {
  compareDates,
  compareLocaleStrings,
  compareNumbers,
  getTableSortIcon,
  stableSortTableRows,
  toggleTableSortConfig,
} from '../../../utils/tableSort';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import {
  EARLY_DISCLOSURE_TABLE_COLUMNS,
  EARLY_DISCLOSURE_TABLE_COLUMN_WIDTHS_KEY,
} from '../../../utils/resizableTableColumns';
import { ResizableColgroup, ResizableTableHead } from '../../../components/ResizableTableHead';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';
import {
  normalizeGhostConfidence01,
  ghostConfidenceTier,
  GHOST_CONFIDENCE_TIER_META,
  ghostConfidencePercentDisplay,
} from '../../ghost-cve/components/GhostCveExposuresSystemPage';

const EDI_TAB_ICONS = {
  exposures: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M4.5 12s2.8-5.5 7.5-5.5S19.5 12 19.5 12 16.7 17.5 12 17.5 4.5 12 4.5 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M12 8.4a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M19.2 12a7.2 7.2 0 0 0-.1-1.1l1.7-1.3-1.6-2.8-2 .8a7.4 7.4 0 0 0-1.9-1.1l-.3-2.1H9l-.3 2.1a7.4 7.4 0 0 0-1.9 1.1l-2-.8-1.6 2.8 1.7 1.3A7.2 7.2 0 0 0 4.8 12c0 .4 0 .7.1 1.1l-1.7 1.3 1.6 2.8 2-.8c.6.4 1.2.8 1.9 1.1l.3 2.1h4.8l.3-2.1c.7-.3 1.3-.7 1.9-1.1l2 .8 1.6-2.8-1.7-1.3c.1-.4.1-.7.1-1.1Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  ),
  apiDocs: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M7 3.75h7.2L18.5 8v12.25H7A1.75 1.75 0 0 1 5.25 18.5V5.5A1.75 1.75 0 0 1 7 3.75Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M14 3.9V8h4.2" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 12h6M9 15.5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
};
export const formatEarlyIntelDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().slice(0, 19).replace('T', ' ');
};

function normalizeUiRole(profile) {
  if (profile?.is_superuser === true) return 'superuser';

  const rawRole = profile?.role;
  const roleStr = String(
    typeof rawRole === 'string'
      ? rawRole
      : (rawRole?.name ?? rawRole?.label ?? rawRole?.role ?? rawRole ?? '')
  ).trim().toLowerCase();

  if (!roleStr) return 'viewer';
  if (roleStr === 'superuser' || roleStr === 'root') return 'superuser';
  if (roleStr === 'partner' || roleStr === '4') return 'partner';
  if (roleStr === 'admin' || roleStr === 'administrator' || roleStr === '1') return 'admin';
  if (roleStr === 'analyst' || roleStr === '2') return 'analyst';
  if (roleStr === 'viewer' || roleStr === '3') return 'viewer';
  return roleStr;
}

const EarlyDisclosuresPage = ({ onViewGhostCveExposures, orgScopeQuery = null, userProfile = null } = {}) => {
  const [ediSection, setEdiSection] = useState('exposures');
  const [ghostCveRows, setGhostCveRows] = useState([]);
  const [ghostCveLoading, setGhostCveLoading] = useState(true);
  const [ghostCveError, setGhostCveError] = useState('');
  const [feedQuery, setFeedQuery] = useState('');
  const [registryFilter, setRegistryFilter] = useState('');
  const [feedRevision, setFeedRevision] = useState(0);

  useEffect(() => {
    let mounted = true;
    setGhostCveLoading(true);
    setGhostCveError('');
    getEarlyIntelSummary(null, orgScopeQuery)
      .then((data) => {
        if (!mounted) return;
        const rows = Array.isArray(data) ? data : [];
        setGhostCveRows(rows.map((item) => {
          const status = String(item.registry_status || item.status || 'UNKNOWN').toUpperCase();
          const firstSeen = item.display_first_seen
            || (item.source_type === 'ghostcves' ? item.evidence_first_seen : null)
            || item.first_seen;
          return {
            cveId: item.cve_id || item.cveId || '',
            status,
            statusVariant: status.toLowerCase().replace(/[^a-z]/g, '_'),
            productHint: item.product_hint || item.productHint || '',
            sourceUrl: item.source_url || '',
            unassessed: item.confidence_assessed === false || (
              item.confidence_assessed == null && item.source_type === 'ghostcves'
              && !item.confidence_reason
            ),
            confidence_score: item.confidence_score ?? 0,
            firstSeen: formatEarlyIntelDateTime(firstSeen),
            daysOld: item.source_type === 'ghostcves' && item.evidence_first_seen
              ? Math.max(0, Math.floor((Date.now() - new Date(item.evidence_first_seen).getTime()) / 86400000))
              : item.days_since_first_seen ?? item.daysOld ?? 0,
            affectedAssets: item.affected_assets_count ?? item.affectedAssets ?? 0,
            internetFacingAssets: item.internet_facing_assets_count ?? 0,
            nvdPublished: Boolean(item.nvd_published),
            unnotified: item.unnotified_exposures_count ?? item.unnotified ?? 0,
          };
        }));
      })
      .catch((err) => {
        if (!mounted) return;
        setGhostCveRows([]);
        setGhostCveError(err?.message || 'Failed to load early intel summary.');
      })
      .finally(() => {
        if (mounted) setGhostCveLoading(false);
      });
    return () => { mounted = false; };
  }, [orgScopeQuery, feedRevision]);

  const EDI_NOTIFICATION_FREQUENCIES = [
    { id: 'immediate', label: 'Immediate' },
    { id: 'daily', label: 'Daily Digest' },
    { id: 'weekly', label: 'Weekly Digest' }
  ];

  const FEATURE_TIERS = [
    {
      id: 'disabled',
      title: 'Disabled',
      description: 'Feature not available'
    },
    {
      id: 'essentials',
      title: 'Essentials',
      description: 'Basic Ghost CVE tracking with manual exposure correlation and subscription-limited usage'
    },
    {
      id: 'professional',
      title: 'Professional',
      description: 'Enhanced matching and higher limits with improved notification rules'
    },
    {
      id: 'enterprise',
      title: 'Enterprise',
      description: 'Full tracking coverage, higher limits, and advanced notification workflows'
    },
    {
      id: 'add_on',
      title: 'Add-on',
      description: 'Custom add-on to existing subscription'
    }
  ];

  const [featureSettings, setFeatureSettings] = useState({
    featureEnabled: false,
    maxExposuresPerMonth: 50,
    notificationsEnabled: false,
    notificationFrequency: 'daily',
    exposuresUsedThisMonth: 0,
    subscriptionTier: 'Essentials'
  });

  const [saveState, setSaveState] = useState({ isSaving: false, message: '' });
  const canManageFeatureSettings = useMemo(() => {
    const cached = (() => {
      try {
        return getCachedProfile() || {};
      } catch {
        return {};
      }
    })();
    const role = normalizeUiRole(userProfile || cached || {});
    return role === 'superuser' || role === 'partner' || role === 'admin';
  }, [userProfile]);

  const isGlobalAdminForEDI = useMemo(() => {
    const cached = (() => {
      try {
        return getCachedProfile() || {};
      } catch {
        return {};
      }
    })();
    const profile = userProfile || cached || {};
    if (profile.is_superuser === true) return true;
    const role = normalizeUiRole(profile);
    return role === 'admin' && String(profile?.organization_id || '').trim() === '1';
  }, [userProfile]);

  useEffect(() => {
    let mounted = true;
    getEarlyIntelFeatureSettings()
      .then((data) => {
        if (!mounted || !data || typeof data !== 'object') return;
        setFeatureSettings((prev) => ({
          ...prev,
          featureEnabled: Boolean(data.featureEnabled),
          maxExposuresPerMonth: Number.isFinite(Number(data.maxExposuresPerMonth))
            ? Math.max(0, Math.floor(Number(data.maxExposuresPerMonth)))
            : prev.maxExposuresPerMonth,
          notificationsEnabled: Boolean(data.notificationsEnabled),
          notificationFrequency: String(data.notificationFrequency || prev.notificationFrequency).trim().toLowerCase(),
          exposuresUsedThisMonth: Number.isFinite(Number(data.exposuresUsedThisMonth))
            ? Math.max(0, Math.floor(Number(data.exposuresUsedThisMonth)))
            : prev.exposuresUsedThisMonth,
          subscriptionTier: String(data.subscriptionTier || prev.subscriptionTier),
        }));
      })
      .catch((err) => {
        if (!mounted) return;
        setSaveState({ isSaving: false, message: err?.message || 'Could not load feature settings.' });
      });

    return () => {
      mounted = false;
    };
  }, []);

  const maxExposuresPerMonth = Math.max(0, Number(featureSettings.maxExposuresPerMonth) || 0);
  const exposuresUsedThisMonth = Math.max(0, Number(featureSettings.exposuresUsedThisMonth) || 0);
  const usageDisplay =
    maxExposuresPerMonth === 0 ? `${exposuresUsedThisMonth}/Unlimited` : `${exposuresUsedThisMonth}/${maxExposuresPerMonth}`;

  const cveRecordUrl = (cveId) => {
    const id = encodeURIComponent(String(cveId).trim());
    return `https://www.cve.org/CVERecord?id=${id}`;
  };

  const stats = useMemo(
    () => {
      const total = ghostCveRows.length;
      const reserved = ghostCveRows.filter((r) => (r.status || '').toUpperCase() === 'RESERVED').length;
      const internetFacing = ghostCveRows.filter((r) => r.internetFacingAssets > 0).length;
      const inNvd = ghostCveRows.filter((r) => r.nvdPublished).length;
      return [
        { label: 'Total Ghost CVEs', value: total, hint: 'Global intelligence feed' },
        { label: 'Reserved Status', value: reserved, hint: 'Confirmed registry status' },
        { label: 'Internet-Facing', value: internetFacing, hint: 'CVEs with scoped internet-facing assets' },
        { label: 'Now in NVD', value: inNvd, hint: 'Confirmed NVD publication' }
      ];
    },
    [ghostCveRows]
  );

  const [ghostSortConfig, setGhostSortConfig] = useState({ key: 'affectedAssets', direction: 'desc' });

  const handleGhostSort = useCallback((key) => {
    setGhostSortConfig((prev) => toggleTableSortConfig(prev, key));
  }, []);

  const getGhostSortIcon = useCallback((key) => getTableSortIcon(ghostSortConfig, key), [ghostSortConfig]);

  const sortedGhostCveRows = useMemo(
    () =>
      stableSortTableRows(ghostCveRows.filter((row) => (
        (!registryFilter || row.status === registryFilter)
        && `${row.cveId} ${row.productHint}`.toLowerCase().includes(feedQuery.trim().toLowerCase())
      )), ghostSortConfig, (a, b, key) => {
        if (key === 'cveId') return compareLocaleStrings(a.cveId, b.cveId);
        if (key === 'status') return compareLocaleStrings(a.status, b.status);
        if (key === 'productHint') return compareLocaleStrings(a.productHint, b.productHint);
        if (key === 'confidence') {
          return (
            normalizeGhostConfidence01(a.confidence_score) - normalizeGhostConfidence01(b.confidence_score)
          );
        }
        if (key === 'firstSeen') return compareDates(a.firstSeen, b.firstSeen);
        if (key === 'daysOld') return compareNumbers(a.daysOld, b.daysOld);
        if (key === 'affectedAssets') return compareNumbers(a.affectedAssets, b.affectedAssets);
        if (key === 'unnotified') return compareNumbers(a.unnotified, b.unnotified);
        return 0;
      }),
    [ghostCveRows, ghostSortConfig, feedQuery, registryFilter]
  );
  const ghostCvePagination = usePaginatedRows(sortedGhostCveRows, TABLE_PAGE_SIZE);
  const visibleGhostCveRows = ghostCvePagination.pageRows;
  const { setPage: setGhostPage } = ghostCvePagination;
  useEffect(() => { setGhostPage(1); }, [feedQuery, registryFilter, ghostSortConfig, orgScopeQuery, setGhostPage]);

  const {
    widths: earlyDisclosureColumnWidths,
    onResizeStart: onEarlyDisclosureColumnResizeStart,
    tableMinWidth: earlyDisclosureTableMinWidth,
    resizingKey: earlyDisclosureColumnResizingKey,
  } = useTableColumnResize(EARLY_DISCLOSURE_TABLE_COLUMNS, EARLY_DISCLOSURE_TABLE_COLUMN_WIDTHS_KEY);

  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [ghostCveLoading, sortedGhostCveRows.length, ediSection]);

  const earlyDisclosureTableClassName =
    'assets-table ghost-cve-table ghost-cve-table--resizable risks-page-table';
  const earlyDisclosureTableStyle = { width: '100%', minWidth: earlyDisclosureTableMinWidth };

  const ediTabs = useMemo(() => {
    const tabs = [
      { id: 'exposures', label: 'Intelligence feed', icon: EDI_TAB_ICONS.exposures },
    ];
    if (canManageFeatureSettings) {
      tabs.push({ id: 'settings', label: 'Feature settings', icon: EDI_TAB_ICONS.settings });
    }
    tabs.push({ id: 'apiDocs', label: 'API docs', icon: EDI_TAB_ICONS.apiDocs });
    return tabs;
  }, [canManageFeatureSettings]);

  if (ediSection === 'exposures' && ghostCveLoading && ghostCveRows.length === 0) {
    return (
      <section className="early-disclosures-page" aria-label="Early Disclosure Intelligence content">
        <PageDataLoader label="Loading Ghost CVEs" />
      </section>
    );
  }

  return (
    <section className="early-disclosures-page" aria-label="Early Disclosure Intelligence content">
      <div className="edi-stats-grid">
        {stats.map((s) => (
          <div key={s.label} className="edi-stat-card">
            <span className="edi-stat-label">{s.label}</span>
            <span className="edi-stat-value" aria-label={`${s.label}: ${s.value}`}>
              {s.value}
            </span>
            <span className="edi-stat-hint">{s.hint}</span>
          </div>
        ))}
      </div>

      <div className="scans-page early-disclosures-subpage">
        <div
          className="scans-network-scope-tabs edi-scope-tabs"
          role="tablist"
          aria-label="Early disclosure sections"
          style={{ '--edi-tab-count': String(ediTabs.length) }}
        >
          <div className="scans-network-scope-tabs-track edi-scope-tabs-track">
            {ediTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`edi-tab-${tab.id === 'apiDocs' ? 'api-docs' : tab.id}`}
                aria-selected={ediSection === tab.id}
                aria-controls={`edi-panel-${tab.id === 'apiDocs' ? 'api-docs' : tab.id}`}
                className={`scans-network-scope-tab${ediSection === tab.id ? ' is-active' : ''}`}
                onClick={() => setEdiSection(tab.id)}
              >
                <span className="scans-network-scope-tab-main">
                  <span className="scans-network-scope-tab-icon" aria-hidden="true">
                    {tab.icon}
                  </span>
                  <span className="scans-network-scope-tab-label">{tab.label}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div
          id="edi-panel-exposures"
          role="tabpanel"
          aria-labelledby="edi-tab-exposures"
          hidden={ediSection !== 'exposures'}
          className="scans-tab-panel"
        >
        <div className="ghost-cve-section">
          <h2 className="assets-section-title">Ghost CVEs</h2>
          <p className="assets-subtitle ghost-cve-section-subtitle">
            Ghost CVEs include early-disclosure or reserved CVE intelligence and correlated exposures, not all standard scan findings shown in Risks.
          </p>
          <div className="edi-feed-toolbar">
            <label className="edi-feed-search">
              <Search size={16} aria-hidden="true" />
              <span className="edi-sr-only">Search CVE or product</span>
              <input type="search" value={feedQuery} placeholder="Search CVE or product" onChange={(event) => setFeedQuery(event.target.value)} />
            </label>
            <label className="edi-feed-status">
              <span className="edi-sr-only">Registry status</span>
              <select value={registryFilter} onChange={(event) => setRegistryFilter(event.target.value)}>
                <option value="">All registry statuses</option>
                {Array.from(new Set(ghostCveRows.map((row) => row.status))).sort().map((status) => (
                  <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </label>
            {(feedQuery || registryFilter) && (
              <button type="button" className="edi-toolbar-button" aria-label="Clear feed filters" title="Clear feed filters" onClick={() => { setFeedQuery(''); setRegistryFilter(''); }}><X size={18} aria-hidden="true" /></button>
            )}
            <button type="button" className="edi-toolbar-button" aria-label="Refresh intelligence feed" title="Refresh intelligence feed" disabled={ghostCveLoading} onClick={() => setFeedRevision((revision) => revision + 1)}><RefreshCw size={18} aria-hidden="true" /></button>
          </div>
          <div className="assets-table-shell assets-table-shell--inventory-body">
            <div
              className="risks-page-table-header-frame"
              style={{ '--risks-header-gutter': `${headerGutterPx}px` }}
            >
              <div ref={tableHeaderRef} className="risks-page-table-header">
                <table
                  className={`${earlyDisclosureTableClassName} risks-page-table--header`}
                  style={earlyDisclosureTableStyle}
                >
                  <ResizableColgroup columns={EARLY_DISCLOSURE_TABLE_COLUMNS} widths={earlyDisclosureColumnWidths} />
                  <ResizableTableHead
                    columns={EARLY_DISCLOSURE_TABLE_COLUMNS}
                    onResizeStart={onEarlyDisclosureColumnResizeStart}
                    resizingKey={earlyDisclosureColumnResizingKey}
                    sortConfig={ghostSortConfig}
                    onSort={handleGhostSort}
                    getSortIcon={getGhostSortIcon}
                    resizeHandleClassName="risks-page-col-resize-handle"
                  />
                </table>
              </div>
              <div className="risks-page-table-header-gutter" aria-hidden="true" />
            </div>
            <div
              ref={tableScrollRef}
              className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
              onScroll={handleTableScroll}
            >
            <table
              className={`${earlyDisclosureTableClassName} risks-page-table--body`}
              style={earlyDisclosureTableStyle}
            >
              <ResizableColgroup columns={EARLY_DISCLOSURE_TABLE_COLUMNS} widths={earlyDisclosureColumnWidths} />
              <tbody>
                {ghostCveLoading ? (
                  <tr>
                    <td colSpan={EARLY_DISCLOSURE_TABLE_COLUMNS.length} className="assets-empty-cell">
                      <BlueXLoader size="small" label="Loading Ghost CVEs" />
                    </td>
                  </tr>
                ) : sortedGhostCveRows.length === 0 ? (
                  <tr>
                    <td colSpan={EARLY_DISCLOSURE_TABLE_COLUMNS.length} className="assets-empty-cell">
                      {ghostCveError ? 'Intelligence feed could not be loaded.' : ghostCveRows.length ? 'No Ghost CVEs match the current filters.' : 'No Ghost CVEs available for this scope.'}
                    </td>
                  </tr>
                ) : visibleGhostCveRows.map((row) => {
                  const conf01 = normalizeGhostConfidence01(
                    row.confidence_score != null ? row.confidence_score : row.confidencePct
                  );
                  const confTier = ghostConfidenceTier(conf01);
                  const confMeta = GHOST_CONFIDENCE_TIER_META[confTier];
                  const confPct = ghostConfidencePercentDisplay(conf01);
                  const confTitle = `${confMeta.meaning}  ${confMeta.severity}  Priority ${confMeta.priority} (${confPct}% match confidence, 01 scale).`;
                  const openExposureDetails = () => {
                    if (typeof onViewGhostCveExposures === 'function' && row.cveId) {
                      onViewGhostCveExposures(row.cveId);
                    }
                  };
                  return (
                  <tr
                    key={row.cveId}
                    className="risks-page-table-row risks-page-table-row--clickable"
                    tabIndex={0}
                    role="link"
                    aria-label={`Open exposure details for ${row.cveId || row.productHint || 'Ghost CVE'}`}
                    onClick={openExposureDetails}
                    onKeyDown={(e) => {
                      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        openExposureDetails();
                      }
                    }}
                  >
                    <td className="ghost-cve-product">{row.productHint || 'Unknown'}</td>
                    <td>
                      <a
                        className="ghost-cve-link"
                        href={cveRecordUrl(row.cveId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.cveId}
                      </a>
                      {row.sourceUrl && (
                        <div>
                          <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                            Source evidence
                          </a>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`ghost-status-badge ghost-status-badge--${row.statusVariant}`}>
                        {row.status === 'NOT_FOUND' ? 'NOT FOUND' : row.status}
                      </span>
                    </td>
                    <td>
                      {row.unassessed ? 'Not assessed' : (
                        <div className="edi-confidence-cell" title={confTitle}>
                          <span className={`edi-confidence-pct edi-confidence-pct--${confTier}`}>
                            {confPct}%
                          </span>
                          <span className="edi-sr-only">
                            {confMeta.meaning}, {confMeta.severity}, priority {confMeta.priority}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="ghost-cve-date">{row.firstSeen}</td>
                    <td>
                      <span className="ghost-days-pill ghost-days-pill--plain">{row.daysOld} days</span>
                    </td>
                    <td className="assets-cell-center">
                      <span className="edi-count-pill edi-count-pill--plain" title="Affected assets">
                        {row.affectedAssets}
                      </span>
                      {typeof onViewGhostCveExposures === 'function' && (
                        <button type="button" className="edi-toolbar-button edi-exposure-action" aria-label={`View exposures for ${row.cveId}`} title={`View exposures for ${row.cveId}`} onClick={(event) => { event.stopPropagation(); openExposureDetails(); }}><Eye size={16} aria-hidden="true" /></button>
                      )}
                    </td>
                    <td className="assets-cell-center">
                      <span className="edi-count-pill edi-count-pill--plain" title="Unnotified">
                        {row.unnotified}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
          <TablePagination {...ghostCvePagination} itemLabel="Ghost CVEs" />
          {ghostCveError ? (
            <div className="profile-settings-message profile-settings-error" role="alert">
              {ghostCveError}
              <button type="button" className="edi-toolbar-button" onClick={() => setFeedRevision((revision) => revision + 1)}><RefreshCw size={16} aria-hidden="true" />Retry intelligence feed</button>
            </div>
          ) : null}
        </div>
        </div>

        <div
          id="edi-panel-settings"
          role="tabpanel"
          aria-labelledby="edi-tab-settings"
          hidden={ediSection !== 'settings'}
          className="scans-tab-panel"
        >
          {!canManageFeatureSettings ? (
            <div className="edi-feature-settings-page" aria-label="Early Disclosures - Feature Settings">
              <div className="edi-feature-settings-access-denied" role="alert">
                Feature Settings are only accessible to administrators.
              </div>
            </div>
          ) : (
          <div className="edi-feature-settings-page" aria-label="Early Disclosures - Feature Settings">
            {!isGlobalAdminForEDI ? (
              <div className="edi-feature-settings-info-banner" role="note">
                Early Disclosure Intelligence settings are managed by xSPECTRE.
                Contact{' '}
                <a href="mailto:support@xspectre.com.au">support@xspectre.com.au</a>{' '}
                to change subscription tier, usage limits, or feature activation.
                You can update your notification preferences below.
              </div>
            ) : null}

            <div className="edi-feature-settings-top">
              <div className="edi-feature-settings-body">
              <div className="edi-feature-settings-col">
                {isGlobalAdminForEDI ? (
                  <div className="edi-feature-settings-card">
                    <div className="edi-feature-settings-section-title">Feature Activation</div>
                    <label className="edi-feature-checkbox-row" title="Toggle early disclosures feature for this organization">
                      <input
                        type="checkbox"
                        className="edi-feature-checkbox"
                        checked={featureSettings.featureEnabled}
                        onChange={(e) => setFeatureSettings((prev) => ({ ...prev, featureEnabled: e.target.checked }))}
                        disabled={saveState.isSaving}
                      />
                      <span className="edi-feature-checkbox-label">
                        Allow this organization to use Early Disclosure Intelligence
                      </span>
                    </label>
                  </div>
                ) : null}

                {isGlobalAdminForEDI ? (
                  <div className="edi-feature-settings-card">
                    <div className="edi-feature-settings-section-title">Usage Limits</div>
                    <div className="edi-feature-settings-field">
                      <div className="edi-feature-settings-field-label">Max Exposures per Month</div>
                      <div className="edi-feature-settings-input-row">
                        <input
                          type="number"
                          className="edi-feature-number-input"
                          min={0}
                          step={1}
                          value={featureSettings.maxExposuresPerMonth}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            const next = Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
                            setFeatureSettings((prev) => ({ ...prev, maxExposuresPerMonth: next }));
                          }}
                          disabled={saveState.isSaving}
                        />
                      </div>
                      <div className="edi-feature-settings-help">0 - Unlimited exposures per month</div>
                    </div>
                  </div>
                ) : null}

                <div className="edi-feature-settings-card">
                  <div className="edi-feature-settings-section-title">Notification Preferences</div>
                  <label className="edi-feature-checkbox-row">
                    <input
                      type="checkbox"
                      className="edi-feature-checkbox"
                      checked={featureSettings.notificationsEnabled}
                      onChange={(e) =>
                        setFeatureSettings((prev) => ({
                          ...prev,
                          notificationsEnabled: e.target.checked
                        }))
                      }
                      disabled={!canManageFeatureSettings || saveState.isSaving}
                    />
                    <span className="edi-feature-checkbox-label">Send email alerts for new exposure</span>
                  </label>

                  <div className="edi-feature-settings-field">
                    <div className="edi-feature-settings-field-label">Notification Frequency</div>
                    <select
                      className="edi-feature-select"
                      value={featureSettings.notificationFrequency}
                      onChange={(e) => setFeatureSettings((prev) => ({ ...prev, notificationFrequency: e.target.value }))}
                      aria-label="Notification frequency"
                      disabled={!canManageFeatureSettings || saveState.isSaving}
                    >
                      {EDI_NOTIFICATION_FREQUENCIES.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="edi-feature-settings-col-right">
                <div className="edi-feature-settings-card edi-feature-right-card">
                  <div className="edi-feature-summary-block">
                    <div className="edi-feature-settings-section-title">Subscription Tier</div>
                    <div className="edi-feature-tier-row">
                      <span className="edi-feature-tier-pill" title="Current subscription tier">
                        {featureSettings.subscriptionTier}
                      </span>
                    </div>
                    <div className="edi-feature-settings-help">
                      {!isGlobalAdminForEDI
                        ? 'Subscription tier is managed by xSPECTRE.'
                        : 'Higher tiers unlock more features and longer usage limits.'}
                    </div>
                  </div>
                  <div className="edi-feature-summary-block">
                    <div className="edi-feature-settings-section-title">Exposures Used This Month</div>
                    <div className="edi-feature-usage-value" aria-label={`Usage: ${usageDisplay}`}>
                      {usageDisplay}
                    </div>
                    {isGlobalAdminForEDI ? (
                      <button
                        type="button"
                        className="edi-feature-reset-btn"
                        onClick={async () => {
                          setSaveState({ isSaving: true, message: '' });
                          try {
                            const result = await resetEarlyIntelFeatureSettingsMonthlyCounter();
                            const settings = result?.settings || {};
                            setFeatureSettings((prev) => ({
                              ...prev,
                              exposuresUsedThisMonth: Number.isFinite(Number(settings?.exposuresUsedThisMonth))
                                ? Math.max(0, Math.floor(Number(settings.exposuresUsedThisMonth)))
                                : 0,
                            }));
                            setSaveState({ isSaving: false, message: 'Monthly exposure counter reset.' });
                          } catch (err) {
                            setSaveState({ isSaving: false, message: err?.message || 'Failed to reset monthly counter.' });
                          }
                        }}
                        disabled={saveState.isSaving}
                      >
                        Reset Monthly Counter
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              </div>

              <div className="edi-feature-settings-footer">
                <div className="edi-feature-settings-actions">
                  <button type="button" className="edi-feature-back-btn" onClick={() => setEdiSection('exposures')}>
                    <span className="edi-feature-back-chevron" aria-hidden="true">
                      &lt;
                    </span>{' '}
                    Back to Dashboard
                  </button>
                  <button
                    type="button"
                    className="edi-feature-save-btn"
                    onClick={async () => {
                      setSaveState({ isSaving: true, message: '' });
                      try {
                        const payload = isGlobalAdminForEDI
                          ? {
                              featureEnabled: featureSettings.featureEnabled,
                              maxExposuresPerMonth: featureSettings.maxExposuresPerMonth,
                              notificationsEnabled: featureSettings.notificationsEnabled,
                              notificationFrequency: featureSettings.notificationFrequency,
                            }
                          : {
                              notificationsEnabled: featureSettings.notificationsEnabled,
                              notificationFrequency: featureSettings.notificationFrequency,
                            };
                        const saved = await updateEarlyIntelFeatureSettings(payload);
                        setFeatureSettings((prev) => ({
                          ...prev,
                          featureEnabled: Boolean(saved?.featureEnabled),
                          maxExposuresPerMonth: Number.isFinite(Number(saved?.maxExposuresPerMonth))
                            ? Math.max(0, Math.floor(Number(saved.maxExposuresPerMonth)))
                            : prev.maxExposuresPerMonth,
                          notificationsEnabled: Boolean(saved?.notificationsEnabled),
                          notificationFrequency: String(saved?.notificationFrequency || prev.notificationFrequency).trim().toLowerCase(),
                          exposuresUsedThisMonth: Number.isFinite(Number(saved?.exposuresUsedThisMonth))
                            ? Math.max(0, Math.floor(Number(saved.exposuresUsedThisMonth)))
                            : prev.exposuresUsedThisMonth,
                          subscriptionTier: String(saved?.subscriptionTier || prev.subscriptionTier),
                        }));
                        setSaveState({
                          isSaving: false,
                          message: isGlobalAdminForEDI ? 'Feature settings saved.' : 'Notification preferences saved.',
                        });
                      } catch (err) {
                        setSaveState({ isSaving: false, message: err?.message || 'Failed to save settings.' });
                      }
                    }}
                    disabled={saveState.isSaving}
                  >
                    {isGlobalAdminForEDI ? 'SAVE SETTINGS' : 'SAVE NOTIFICATION PREFERENCES'}
                  </button>
                </div>
                {saveState.message ? (
                  <div className="edi-feature-settings-message" role="status" aria-live="polite">
                    {saveState.message}
                  </div>
                ) : null}
              </div>
            </div>

            {isGlobalAdminForEDI ? (
              <div className="edi-feature-tiers-bar" aria-label="Feature Tiers">
                <div className="edi-feature-tiers-title">Feature Tiers</div>
                <ul className="edi-feature-tiers-list">
                  {FEATURE_TIERS.map((t) => {
                    const isCurrent = t.title === featureSettings.subscriptionTier;
                    return (
                      <li key={t.id} className={`edi-feature-tier-item ${isCurrent ? 'is-current' : ''}`}>
                        <span className="edi-feature-tier-dot" aria-hidden="true" />
                        <span>
                          <strong>{t.title}:</strong> {t.description}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
          )}
        </div>

        <div
          id="edi-panel-api-docs"
          role="tabpanel"
          aria-labelledby="edi-tab-api-docs"
          hidden={ediSection !== 'apiDocs'}
          className="scans-tab-panel"
        >
          <div className="edi-placeholder-panel">
            <p className="simple-page-subtitle edi-placeholder-text">
              Early Intel APIs let your systems pull summary and exposure data, and mark exposures as notified.
              Full request and response details are available in the platform API documentation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EarlyDisclosuresPage;
